/**
 * Cross-component registry of companies whose underwriting refresh the user has
 * just triggered from the table.
 *
 * Why this exists: the per-row refresh button lives inside a MUI DataGrid cell,
 * which re-mounts on every re-render/poll — so local React state can't reliably
 * keep the button spinning for the whole run. This module-level store survives
 * those re-mounts, and it also lets the summary page know to keep polling so the
 * row's live `underwritingInProgress` state actually surfaces (and then clears).
 */

const COMPANY_STORAGE_KEY = "crediarc:refreshing-companies:v1";
const TENANT_STORAGE_KEY = "crediarc:refreshing-tenants:v1";
const storage = typeof window !== "undefined" ? window.localStorage : null;

const readMap = (key, maxAge) => {
  try {
    const values = JSON.parse(storage?.getItem(key) || "{}");
    const cutoff = Date.now() - maxAge;
    return new Map(Object.entries(values).filter(([, timestamp]) => Number(timestamp) >= cutoff));
  } catch (_) { return new Map(); }
};

// Keep the registry across route changes, reloads, and separate dashboard tabs.
export const MAX_REFRESH_MS = 30 * 60 * 1000;
const startedAt = readMap(COMPANY_STORAGE_KEY, MAX_REFRESH_MS); // companyId -> epoch ms
const tenantStartedAt = readMap(TENANT_STORAGE_KEY, MAX_REFRESH_MS); // tenantId -> epoch ms
const listeners = new Set();

const persist = (key, values) => {
  try { storage?.setItem(key, JSON.stringify(Object.fromEntries(values))); } catch (_) {}
};

const notify = () => {
  listeners.forEach((fn) => { try { fn(); } catch (_) { /* ignore */ } });
};

export const markRefreshing = (companyId) => {
  if (!companyId) return;
  startedAt.set(companyId, Date.now());
  persist(COMPANY_STORAGE_KEY, startedAt);
  notify();
};

export const clearRefreshing = (companyId) => {
  if (startedAt.delete(companyId)) {
    persist(COMPANY_STORAGE_KEY, startedAt);
    notify();
  }
};

export const isRefreshing = (companyId) => startedAt.has(companyId);

export const anyRefreshing = () => startedAt.size > 0;

export const refreshStartedAt = (companyId) => startedAt.get(companyId) || null;

export const markTenantRefreshing = (tenantId) => {
  if (!tenantId) return;
  tenantStartedAt.set(tenantId, Date.now());
  persist(TENANT_STORAGE_KEY, tenantStartedAt);
  notify();
};

export const clearTenantRefreshing = (tenantId) => {
  if (tenantStartedAt.delete(tenantId)) {
    persist(TENANT_STORAGE_KEY, tenantStartedAt);
    notify();
  }
};

export const isTenantRefreshing = (tenantId) => {
  const timestamp = tenantStartedAt.get(tenantId);
  if (!timestamp) return false;
  if (Date.now() - timestamp <= MAX_REFRESH_MS) return true;
  clearTenantRefreshing(tenantId);
  return false;
};

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (![COMPANY_STORAGE_KEY, TENANT_STORAGE_KEY].includes(event.key)) return;
    const target = event.key === COMPANY_STORAGE_KEY ? startedAt : tenantStartedAt;
    const fresh = readMap(event.key, MAX_REFRESH_MS);
    target.clear();
    fresh.forEach((timestamp, id) => target.set(id, timestamp));
    notify();
  });
}

// Subscribe to registry changes; returns an unsubscribe fn.
export const subscribeRefreshing = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
